import type { PatientSignUpAttribution, PatientSignUpData } from '@dev-console/types';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { EmptyState, ErrorState, LoadingState } from '@/components/ui/state-views';
import { getPatientSignUp } from '@/config/api-helpers';
import { formatDate } from '@/utils/format';

const WAITLIST_STATUS_COLORS: Record<string, string> = {
  ACTIVE: 'bg-blue-50 text-blue-700',
  FULFILLED: 'bg-green-50 text-green-700',
  EXPIRED: 'bg-neutral-100 text-neutral-600',
};

const INSURANCE_STATUS_COLORS: Record<string, string> = {
  ACTIVE: 'bg-green-50 text-green-700',
  INACTIVE: 'bg-red-50 text-red-700',
  ERRORED: 'bg-red-50 text-red-700',
  UNDETERMINED: 'bg-amber-50 text-amber-700',
};

function Field({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div>
      <span className='text-neutral-500'>{label}:</span> <span className='text-neutral-900'>{value || '\u2014'}</span>
    </div>
  );
}

function StatusBadge({ status, colors }: { status: string; colors: Record<string, string> }) {
  return (
    <span
      className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${
        colors[status] ?? 'bg-neutral-100 text-neutral-600'
      }`}
    >
      {status.replace(/_/g, ' ')}
    </span>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className='space-y-3'>
      <h4 className='text-sm font-medium text-neutral-900'>{title}</h4>
      {children}
    </div>
  );
}

function formatCurrency(cents: number | null) {
  if (cents == null) return '\u2014';
  return `$${(cents / 100).toFixed(2)}`;
}

function hasAttribution(a: PatientSignUpAttribution) {
  return a.utmSource || a.utmMedium || a.utmCampaign || a.gclid || a.fbclid || a.ttclid || a.oppref || a.irclickid;
}

export function SignUpTab({ patientId }: { patientId: number }) {
  const navigate = useNavigate();
  const { data, error, isLoading, refetch } = useQuery({
    queryKey: ['patient-sign-up', patientId],
    queryFn: () => getPatientSignUp(patientId),
  });

  if (error)
    return <ErrorState message={error?.message ?? ''} onRetry={() => refetch()} title='Sign-up data is unavailable' />;
  if (isLoading) return <LoadingState message='Loading sign-up data' />;
  if (!data) return <EmptyState message='No sign-up data available.' />;

  const { patient, signUpLead, insuranceEligibility, waitlist } = data;

  return (
    <div className='divide-y divide-neutral-100'>
      {/* Funnel Timeline */}
      <div className='p-4'>
        <Section title='Funnel Timeline'>
          <div className='grid grid-cols-1 gap-x-6 gap-y-2 text-sm text-neutral-700 md:grid-cols-3'>
            <Field label='Sign-up Started' value={signUpLead ? formatDate(signUpLead.createdAt) : null} />
            <Field
              label='Sign-up Completed'
              value={signUpLead?.completedAt ? formatDate(signUpLead.completedAt) : null}
            />
            <Field
              label='Abandoned'
              value={signUpLead?.abandonRecordedAt ? formatDate(signUpLead.abandonRecordedAt) : null}
            />
            <Field label='Patient Created' value={formatDate(patient.patientCreatedAt)} />
            <Field
              label='First Appt Booked'
              value={patient.firstApptCreatedAt ? formatDate(patient.firstApptCreatedAt) : null}
            />
            <Field label='Creation State' value={patient.patientCreationState?.replace(/_/g, ' ') ?? null} />
            <Field label='Last Step' value={signUpLead?.currentStepId} />
            <Field label='Variation' value={signUpLead?.variation} />
            <Field label='Landing Page' value={signUpLead?.landingPageVariation} />
            <Field label='Seen Onboarding' value={patient.hasSeenOnboarding ? 'Yes' : 'No'} />
            <Field label='Verification' value={signUpLead?.verificationMethod} />
            <Field label='Verification Status' value={signUpLead?.verificationStatus} />
          </div>
        </Section>
      </div>

      {/* Attribution */}
      {signUpLead && (
        <div className='p-4'>
          <Section title='Attribution & Referral'>
            <div className='grid grid-cols-1 gap-x-6 gap-y-2 text-sm text-neutral-700 md:grid-cols-3'>
              <Field label='Referral Source Type' value={signUpLead.referralSourceType} />
              <Field label='Referral Source' value={signUpLead.referralSource} />
              <Field label='Referral Subtype' value={signUpLead.referralSourceSubtype} />
              <Field label='Referral Name' value={signUpLead.referralName} />
              {signUpLead.referringProviderNpi && (
                <>
                  <Field label='Referring Provider Type' value={signUpLead.referringProviderType} />
                  <Field label='Referring Provider NPI' value={signUpLead.referringProviderNpi} />
                  <Field
                    label='Referring Provider Location'
                    value={
                      [signUpLead.referringProviderCity, signUpLead.referringProviderState]
                        .filter(Boolean)
                        .join(', ') || null
                    }
                  />
                </>
              )}
            </div>

            {signUpLead.providerReferral && (
              <div className='bg-neutral-25 mt-3 rounded-md border border-neutral-100 p-3'>
                <p className='mb-1 text-xs font-medium text-neutral-500'>Provider Referral Link</p>
                <div className='grid grid-cols-1 gap-x-6 gap-y-1 text-sm text-neutral-700 md:grid-cols-3'>
                  <Field label='Type' value={signUpLead.providerReferral.referralType} />
                  {signUpLead.providerReferral.referral && (
                    <>
                      <Field label='Provider' value={signUpLead.providerReferral.referral.providerName} />
                      <Field label='NPI' value={signUpLead.providerReferral.referral.providerNpi} />
                    </>
                  )}
                </div>
              </div>
            )}

            {signUpLead.patientReferral && (
              <div className='bg-neutral-25 mt-3 rounded-md border border-neutral-100 p-3'>
                <p className='mb-1 text-xs font-medium text-neutral-500'>Patient-to-Patient Referral</p>
                <div className='text-sm text-neutral-700'>
                  Referred by{' '}
                  <button
                    className='text-neutral-900 underline decoration-neutral-300 hover:text-neutral-700'
                    onClick={() => navigate(`/patients/${signUpLead.patientReferral!.referringPatientId}`)}
                    type='button'
                  >
                    {signUpLead.patientReferral.referringPatientName}
                  </button>
                </div>
              </div>
            )}

            {hasAttribution(signUpLead.attribution) && (
              <div className='bg-neutral-25 mt-3 rounded-md border border-neutral-100 p-3'>
                <p className='mb-1 text-xs font-medium text-neutral-500'>UTM / Click Tracking</p>
                <div className='grid grid-cols-1 gap-x-6 gap-y-1 text-sm text-neutral-700 md:grid-cols-3'>
                  <Field label='Source' value={signUpLead.attribution.utmSource} />
                  <Field label='Medium' value={signUpLead.attribution.utmMedium} />
                  <Field label='Campaign' value={signUpLead.attribution.utmCampaign} />
                  <Field label='Content' value={signUpLead.attribution.utmContent} />
                  <Field label='Term' value={signUpLead.attribution.utmTerm} />
                  {signUpLead.attribution.gclid && (
                    <Field label='Google Click ID' value={signUpLead.attribution.gclid} />
                  )}
                  {signUpLead.attribution.fbclid && (
                    <Field label='Facebook Click ID' value={signUpLead.attribution.fbclid} />
                  )}
                  {signUpLead.attribution.ttclid && (
                    <Field label='TikTok Click ID' value={signUpLead.attribution.ttclid} />
                  )}
                  {signUpLead.attribution.oppref && (
                    <Field label='OpenAI Click ID' value={signUpLead.attribution.oppref} />
                  )}
                  {signUpLead.attribution.irclickid && (
                    <Field label='Impact Click ID' value={signUpLead.attribution.irclickid} />
                  )}
                </div>
              </div>
            )}
          </Section>
        </div>
      )}

      {/* Insurance & Eligibility */}
      <div className='p-4'>
        <Section title='Insurance & Eligibility'>
          {signUpLead && (
            <div className='mb-3 grid grid-cols-1 gap-x-6 gap-y-2 text-sm text-neutral-700 md:grid-cols-3'>
              <Field label='Insurance (at signup)' value={signUpLead.insuranceCompany} />
              <Field
                label='Preauth Successful'
                value={
                  signUpLead.wasPreauthedSuccessfully == null
                    ? null
                    : signUpLead.wasPreauthedSuccessfully
                    ? 'Yes'
                    : 'No'
                }
              />
            </div>
          )}
          {insuranceEligibility ? (
            <div className='bg-neutral-25 rounded-md border border-neutral-100 p-3'>
              <p className='mb-2 text-xs font-medium text-neutral-500'>Latest Eligibility Check</p>
              <div className='grid grid-cols-1 gap-x-6 gap-y-2 text-sm text-neutral-700 md:grid-cols-3'>
                <div>
                  <span className='text-neutral-500'>Status:</span>{' '}
                  {insuranceEligibility.status ? (
                    <StatusBadge status={insuranceEligibility.status} colors={INSURANCE_STATUS_COLORS} />
                  ) : (
                    <span className='text-neutral-500'>—</span>
                  )}
                </div>
                <Field label='Workflow' value={insuranceEligibility.workflowStatus} />
                <Field label='Network' value={insuranceEligibility.networkStatus} />
                <Field label='Estimated Cost' value={formatCurrency(insuranceEligibility.estimatedCost)} />
                <Field label='Payer' value={insuranceEligibility.payerName} />
                <Field label='Plan' value={insuranceEligibility.planName} />
                <Field label='Plan Status' value={insuranceEligibility.planStatus} />
                <Field
                  label='Coverage Start'
                  value={
                    insuranceEligibility.coverageStartDate ? formatDate(insuranceEligibility.coverageStartDate) : null
                  }
                />
                <Field label='Source' value={insuranceEligibility.source} />
                <Field label='Checked' value={formatDate(insuranceEligibility.createdAt)} />
              </div>
            </div>
          ) : (
            <p className='text-sm text-neutral-500'>No eligibility checks on record.</p>
          )}
        </Section>
      </div>

      {/* Health Goals */}
      {signUpLead && signUpLead.healthProfile.wantHelpWith.length > 0 && (
        <div className='p-4'>
          <Section title='Health Goals (at signup)'>
            <div className='grid grid-cols-1 gap-x-6 gap-y-2 text-sm text-neutral-700 md:grid-cols-3'>
              <div className='md:col-span-3'>
                <span className='text-neutral-500'>Wants help with:</span>{' '}
                {signUpLead.healthProfile.wantHelpWith.join(', ')}
              </div>
              <Field label='Commitment Level' value={signUpLead.healthProfile.levelOfCommitment} />
              <Field label='Activity Level' value={signUpLead.healthProfile.activityLevel} />
              <Field label='Eating Habits' value={signUpLead.healthProfile.eatingHabits} />
            </div>
          </Section>
        </div>
      )}

      {/* Waitlist */}
      {waitlist.length > 0 && (
        <div className='p-4'>
          <Section title='Waitlist History'>
            <div className='overflow-x-auto'>
              <table className='min-w-full divide-y divide-neutral-100 text-left text-sm'>
                <thead className='text-neutral-600'>
                  <tr>
                    <th className='py-2 pr-4 font-medium'>Type</th>
                    <th className='py-2 pr-4 font-medium'>Status</th>
                    <th className='py-2 pr-4 font-medium'>Priority</th>
                    <th className='py-2 pr-4 font-medium'>Created</th>
                    <th className='py-2 pr-4 font-medium'>Last Checked</th>
                  </tr>
                </thead>
                <tbody className='divide-y divide-neutral-100'>
                  {waitlist.map((w: PatientSignUpData['waitlist'][number]) => (
                    <tr key={w.id}>
                      <td className='py-2 pr-4 text-neutral-700'>
                        {w.appointmentType ? w.appointmentType.replace(/_/g, ' ') : '\u2014'}
                      </td>
                      <td className='py-2 pr-4'>
                        {w.status ? (
                          <StatusBadge status={w.status} colors={WAITLIST_STATUS_COLORS} />
                        ) : (
                          <span className='text-neutral-500'>—</span>
                        )}
                      </td>
                      <td className='py-2 pr-4 text-neutral-700'>{w.priority}</td>
                      <td className='py-2 pr-4 text-neutral-700'>{formatDate(w.createdAt)}</td>
                      <td className='py-2 pr-4 text-neutral-700'>
                        {w.lastCheckedAt ? formatDate(w.lastCheckedAt) : '\u2014'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>
        </div>
      )}

      {!signUpLead && (
        <div className='p-4'>
          <p className='text-sm text-neutral-500'>No sign-up lead found for this patient's email address.</p>
        </div>
      )}
    </div>
  );
}
